import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import { InputFieldProps } from 'components/InputField/InputField.component';
import utils from 'utils';
import { ObjectValues } from 'types';

export const EMAIL_PHONE_INPUT_TYPE = {
  EMAIL: 'email',
  PHONE: 'phone',
  UNKNOWN: 'unknown',
} as const;
export type EmailPhoneInputFieldType = ObjectValues<
  typeof EMAIL_PHONE_INPUT_TYPE
>;

export type EmailPhoneInputFieldProps = Omit<
  InputFieldProps,
  'type' | 'numberStyle'
> & {
  emailPlaceholder?: string;
  phonePlaceholder?: string;
  onInputTypeChange?: (type: EmailPhoneInputFieldType) => void;
};

const EmailPhoneInputField: React.FC<EmailPhoneInputFieldProps> = (props) => {
  const {
    className,
    label,
    placeholder,
    emailPlaceholder,
    phonePlaceholder,
    onInputTypeChange,
    input,
    meta,
    ...rest
  } = props;

  const { t } = useTranslation();

  // Initialize detected type based on existing input value
  const initialType = useMemo(() => {
    if (!input?.value) return EMAIL_PHONE_INPUT_TYPE.UNKNOWN;

    return utils.getEmailPhoneInputType(input.value);
  }, [input?.value]);

  const [detectedType, setDetectedType] =
    useState<EmailPhoneInputFieldType>(initialType);

  // Update detected type when input value changes externally
  useEffect(() => {
    if (input?.value) {
      const newType = utils.getEmailPhoneInputType(input.value);
      if (newType !== detectedType) {
        setDetectedType(newType);
        onInputTypeChange?.(newType);
      }
    }
  }, [input?.value, detectedType, onInputTypeChange]);

  const classes = classNames('email-phone-input-field', className);

  const handleChangeHandle = useCallback(
    (value: string) => {
      const newType = utils.getEmailPhoneInputType(value);

      if (newType !== detectedType) {
        setDetectedType(newType);
        onInputTypeChange?.(newType);
      }

      // Return the value as-is (InputField will handle the onChange call)
      return value;
    },
    [detectedType, onInputTypeChange],
  );

  const currentPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;

    switch (detectedType) {
      case EMAIL_PHONE_INPUT_TYPE.EMAIL:
        return emailPlaceholder || t('email');
      case EMAIL_PHONE_INPUT_TYPE.PHONE:
        return phonePlaceholder || t('phoneNumber');
      default:
        return emailPlaceholder || t('emailOrPhone');
    }
  }, [placeholder, detectedType, emailPlaceholder, phonePlaceholder, t]);

  const currentLabel = useMemo(() => {
    if (label) return label;

    switch (detectedType) {
      case EMAIL_PHONE_INPUT_TYPE.EMAIL:
        return t('email');
      case EMAIL_PHONE_INPUT_TYPE.PHONE:
        return t('phoneNumber');
      default:
        return t('emailOrPhone');
    }
  }, [label, detectedType, t]);

  // Determine input props based on detected type
  const inputProps = useMemo(() => {
    const baseProps = {
      className: classes,
      label: currentLabel,
      placeholder: currentPlaceholder,
      input,
      meta,
      onChangeHandle: handleChangeHandle,
      ...rest,
    };

    switch (detectedType) {
      case EMAIL_PHONE_INPUT_TYPE.EMAIL:
        return {
          ...baseProps,
          type: 'email' as const,
        };
      case EMAIL_PHONE_INPUT_TYPE.PHONE:
        return {
          ...baseProps,
          numberStyle: 'phoneNumber' as const,
        };
      default:
        return baseProps;
    }
  }, [
    classes,
    currentLabel,
    currentPlaceholder,
    input,
    handleChangeHandle,
    meta,
    rest,
    detectedType,
  ]);

  return <InputField {...inputProps} />;
};

export default EmailPhoneInputField;
